import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { verifyMfaChallenge } from "@/lib/mfa.functions";

export const Route = createFileRoute("/mfa-verify")({
  ssr: false,
  validateSearch: (search) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : "/",
  }),
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session?.user) throw redirect({ to: "/auth" });
  },
  head: () => ({
    meta: [
      { title: "Verify your identity — The Hospitality Reserve" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MfaVerifyPage,
});

function MfaVerifyPage() {
  const { redirect: to } = Route.useSearch();
  const navigate = useNavigate();
  const verify = useServerFn(verifyMfaChallenge);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res = (await verify({ data: { code: code.trim() } })) as {
        ok?: boolean;
        error?: string;
      };
      if (res.error) return toast.error(res.error);
      const { data } = await supabase.auth.getUser();
      if (data.user) window.sessionStorage.setItem(`thr_mfa_ok_${data.user.id}`, "1");
      const target = to && to.startsWith("/") ? to : "/";
      navigate({ to: target });
    } finally {
      setBusy(false);
    }
  }

  async function onSignOut() {
    try {
      await supabase.auth.signOut();
    } catch { /* ignore */ }
    navigate({ to: "/auth" });
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-lg border bg-card p-6 space-y-4"
      >
        <div>
          <h1 className="text-xl font-semibold">Two-factor authentication</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enter the 6-digit code from your authenticator app, or a recovery code.
          </p>
        </div>
        <div>
          <Label htmlFor="code">Code</Label>
          <Input
            id="code"
            autoFocus
            autoComplete="one-time-code"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
          />
        </div>
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? "Verifying…" : "Verify"}
        </Button>
        <button
          type="button"
          onClick={onSignOut}
          className="text-xs text-muted-foreground hover:underline w-full text-center"
        >
          Sign out
        </button>
      </form>
    </div>
  );
}
